import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsultorioEquipo } from '../../hooks/useConsultorioEquipo';
import { getCitasConsultorio, getPacientesConsultorio } from '../../data/consultorioData';
import { ESTADO_CITA_LABEL, getMiembroNombre, getPacNombre } from '../../utils/citasHelpers';

export const CITAS_CHANGED_EVENT = 'consultorio-citas-changed';

const HORIZON_DAYS = 14;
const MAX_ITEMS = 25;

function formatCitaWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.round((day - today) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return `Hoy ${time}`;
  if (diffDays === 1) return `Mañana ${time}`;
  const datePart = d.toLocaleDateString('es-EC', { weekday: 'short', day: 'numeric', month: 'short' });
  return `${datePart} · ${time}`;
}

function estadoClass(estado) {
  const e = estado || 'pendiente';
  return `csl-notif-estado csl-notif-estado--${e}`;
}

export default function ConsultorioCitasNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { equipo, equipoIds } = useConsultorioEquipo(user);
  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const loadCitas = useCallback(async () => {
    if (!user?.id) {
      setCitas([]);
      setPacientes([]);
      return;
    }
    setLoading(true);
    try {
      const ids = equipoIds.length ? equipoIds : [user.id];
      const [cList, pList] = await Promise.all([
        getCitasConsultorio(ids),
        getPacientesConsultorio(ids),
      ]);
      setCitas(Array.isArray(cList) ? cList : []);
      setPacientes(Array.isArray(pList) ? pList : []);
      setLoadedOnce(true);
    } catch {
      setCitas([]);
      setPacientes([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, equipoIds]);

  useEffect(() => {
    if (!user?.id) return;
    loadCitas();
  }, [user?.id, equipoIds.join(','), loadCitas]);

  useEffect(() => {
    const onChanged = () => loadCitas();
    window.addEventListener(CITAS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CITAS_CHANGED_EVENT, onChanged);
  }, [loadCitas]);

  const upcoming = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now);
    horizon.setDate(horizon.getDate() + HORIZON_DAYS);
    return (citas || [])
      .filter((c) => {
        const estado = c.estado || 'pendiente';
        if (estado === 'cancelada' || estado === 'realizada') return false;
        const start = new Date(c.fechaHora);
        return !Number.isNaN(start.getTime()) && start >= now && start <= horizon;
      })
      .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora))
      .slice(0, MAX_ITEMS);
  }, [citas]);

  const badgeCount = upcoming.length;

  const handleToggle = (next) => {
    if (next) loadCitas();
  };

  return (
    <Dropdown align="end" className="csl-notif-dropdown" onToggle={handleToggle}>
      <Dropdown.Toggle
        variant="link"
        id="csl-citas-notif"
        className="csl-topbar-icon csl-topbar-icon--notif"
        aria-label={
          badgeCount
            ? `${badgeCount} cita${badgeCount === 1 ? '' : 's'} próxima${badgeCount === 1 ? '' : 's'}`
            : 'Citas próximas'
        }
      >
        <i className="bi bi-bell" />
        {badgeCount > 0 ? (
          <span className="csl-notif-badge" aria-hidden>
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu className="csl-notif-menu shadow">
        <div className="csl-notif-menu-header">
          <div>
            <div className="csl-notif-menu-title">Próximas citas</div>
            <div className="csl-notif-menu-sub">
              {badgeCount === 0
                ? `Sin citas en los próximos ${HORIZON_DAYS} días`
                : `${badgeCount} en los próximos ${HORIZON_DAYS} días`}
            </div>
          </div>
          <button
            type="button"
            className="csl-notif-refresh"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              loadCitas();
            }}
            title="Actualizar"
            aria-label="Actualizar citas"
            disabled={loading}
          >
            <i className={`bi ${loading ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} />
          </button>
        </div>

        <div className="csl-notif-list">
          {loading && !loadedOnce ? (
            <div className="csl-notif-empty">
              <Spinner size="sm" animation="border" />
            </div>
          ) : upcoming.length === 0 ? (
            <div className="csl-notif-empty">
              <i className="bi bi-calendar-check" />
              <span>No hay citas pendientes próximas</span>
            </div>
          ) : (
            upcoming.map((c) => {
              const paciente = getPacNombre(pacientes, c.idPaciente);
              const profesional =
                equipo.length > 1 ? getMiembroNombre(equipo, c.idDoctor) : null;
              return (
                <Dropdown.Item
                  key={c.id}
                  as="button"
                  type="button"
                  className="csl-notif-item"
                  onClick={() => navigate('/consultorio/citas')}
                >
                  <div className="csl-notif-item-top">
                    <span className="csl-notif-item-time">{formatCitaWhen(c.fechaHora)}</span>
                    <span className={estadoClass(c.estado)}>
                      {ESTADO_CITA_LABEL[c.estado] || c.estado || 'Pendiente'}
                    </span>
                  </div>
                  <div className="csl-notif-item-patient">{paciente}</div>
                  {c.motivo ? (
                    <div className="csl-notif-item-motivo">{c.motivo}</div>
                  ) : null}
                  {profesional ? (
                    <div className="csl-notif-item-doc">
                      <i className="bi bi-person-badge" />
                      {profesional}
                    </div>
                  ) : null}
                  {c.idPaciente ? (
                    <Link
                      to={`/consultorio/paciente/${c.idPaciente}`}
                      className="csl-notif-item-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver ficha
                    </Link>
                  ) : null}
                </Dropdown.Item>
              );
            })
          )}
        </div>

        <Dropdown.Divider className="my-0" />
        <Dropdown.Item as={Link} to="/consultorio/citas" className="csl-notif-footer">
          <i className="bi bi-calendar3" />
          Abrir calendario
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
